import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {FilterInput} from 'src/components/filter-input';
import {DEBOUNCE_MS} from 'src/constants';
import {useVendors} from 'src/hooks/use-vendors';
import {isVendorAdminRole} from 'src/utils/role-utils';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import Group from '@pb/kit-component-group';

import type {ApprovalRequestSearchCriteria} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import type {User} from 'src/openapi/admin/models/User';

type Props = {
    criteria: ApprovalRequestSearchCriteria,
    onChange: (criteria: Partial<ApprovalRequestSearchCriteria>) => void,
    currentUser: User | null,
};

const vendorInputWidth = 260;
const filterInputWidth = 260;
const MIN_EMAIL_LEN = 3;

export const RequestTopFilters: React.FC<Props> = ({criteria, onChange, currentUser}) => {
    const isVendorAdmin = isVendorAdminRole(currentUser?.role?.id);
    const {vendors} = useVendors(currentUser !== null && !isVendorAdmin);
    const [vendorInput, setVendorInput] = useState('');
    const [vendorShown, setVendorShown] = useState(false);

    useEffect(() => {
        if (!criteria.vendorId) {
            setVendorInput('');
            return;
        }
        const match = vendors.find(v => v.id === criteria.vendorId);
        if (match) {
            setVendorInput(match.vendorName);
        }
    }, [criteria.vendorId, vendors]);

    const handleEmailChange = useCallback((value: string) => {
        const trimmed = value.trim();
        if (trimmed && trimmed.length < MIN_EMAIL_LEN) {
            onChange({email: undefined, page: 1});
            return;
        }
        onChange({email: trimmed || undefined, page: 1});
    }, [onChange]);

    const handleEmailClear = useCallback(() => {
        onChange({email: undefined, page: 1});
    }, [onChange]);

    const filteredVendors = useMemo(() => {
        const term = vendorInput.trim().toLowerCase();
        if (!term) {
            return vendors;
        }
        return vendors.filter(v => v.vendorName.toLowerCase().includes(term));
    }, [vendorInput, vendors]);

    const handleVendorChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(e => {
        const value = e.currentTarget.value;
        setVendorInput(value);
        setVendorShown(true);
    }, []);

    const handleVendorSelect = useCallback((vendor: {id: string, vendorName: string}) => {
        setVendorInput(vendor.vendorName);
        setVendorShown(false);
        onChange({vendorId: vendor.id, page: 1});
    }, [onChange]);

    const handleVendorClear = useCallback(() => {
        setVendorInput('');
        setVendorShown(false);
        onChange({vendorId: undefined, page: 1});
    }, [onChange]);

    return (
        <Group style={{paddingLeft: 16, paddingBottom: 16}} direction="h" gap="m" wrap="wrap">
            <Group direction="v" gap="xxs">
                <span>Email</span>
                <FilterInput
                    id="request-email-filter"
                    value={criteria.email ?? ''}
                    onChange={handleEmailChange}
                    onClear={handleEmailClear}
                    timeout={DEBOUNCE_MS}
                    width={filterInputWidth}
                    placeholder="Search by email (min. 3 characters)"
                />
            </Group>
            {isVendorAdmin ? null : (
                <Group direction="v" gap="xxs">
                    <span>Vendor</span>
                    <div style={{width: vendorInputWidth}}>
                        <Autosuggest
                            id="request-vendor-filter"
                            value={vendorInput}
                            shown={vendorShown}
                            setShown={setVendorShown}
                            onClick={() => setVendorShown(true)}
                            onChange={handleVendorChange}
                            placeholder="Type to search vendor"
                            onItemSelect={handleVendorSelect}
                            onClear={handleVendorClear}>
                            {filteredVendors.length > 0 && (
                                <div id="request-vendor-suggest-list">
                                    {filteredVendors.map(vendor => (
                                        <AutosuggestItem
                                            key={vendor.id}
                                            value={vendor}
                                            caption={vendor.vendorName}
                                        />
                                    ))}
                                </div>
                            )}
                            {filteredVendors.length === 0 && vendorInput.trim() ? <AutosuggestNoResults/> : null}
                        </Autosuggest>
                    </div>
                </Group>
            )}
        </Group>
    );
};
